import { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useContent } from '@/contexts/ContentContext';
import { useEditMode } from '@/contexts/EditModeContext';
import { toast } from 'sonner';
import { Pencil } from 'lucide-react';
import { RichTextEditor } from './RichTextEditor';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import DOMPurify from 'dompurify';

interface InlineContentEditorProps {
  contentKey: string;
  children: React.ReactNode;
  category?: string;
}

export function InlineContentEditor({ contentKey, children, category }: InlineContentEditorProps) {
  const [open, setOpen] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [editorMode, setEditorMode] = useState<'plain' | 'rich'>('plain');
  const [isDemoUser, setIsDemoUser] = useState(false);
  const { updateContent, getContent } = useContent();
  const { isEditMode } = useEditMode();

  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsDemoUser(user?.email === 'support@therappi.app');
    };
    checkUser();
  }, []);

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      setEditedContent(getContent(contentKey, ''));
    }
    setOpen(isOpen);
  };

  const handleSave = async () => {
    try {
      await updateContent(contentKey, editedContent);
      toast.success('Content updated');
      setOpen(false);
    } catch (error) {
      toast.error('Failed to update content');
      console.error(error);
    }
  };

  // Only show editing UI if edit mode is active and not demo user
  if (!isEditMode || isDemoUser) {
    // Strip inline color styles for display to use semantic tokens and sanitize
    const cleanContent = typeof children === 'string' 
      ? children.replace(/style="[^"]*color[^"]*"/gi, '').replace(/color:\s*[^;]+;?/gi, '')
      : String(children);
    return <span className="text-foreground" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleanContent) }} />;
  }

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <span 
          className="relative inline-block cursor-pointer hover:bg-primary/10 hover:outline hover:outline-2 hover:outline-primary/30 rounded px-1 py-0.5 transition-all group border-2 border-dashed border-primary/40 hover:border-primary bg-primary/5 z-[200] pointer-events-auto text-foreground"
        >
          <span 
            className="text-foreground"
            dangerouslySetInnerHTML={{ 
              __html: DOMPurify.sanitize(
                (typeof children === 'string' ? children : String(children))
                  .replace(/style="[^"]*color[^"]*"/gi, '')
                  .replace(/color:\s*[^;]+;?/gi, '')
              )
            }} 
          />
          <Pencil className="inline-block w-3 h-3 ml-1 opacity-80 group-hover:opacity-100 transition-opacity text-primary" />
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-[600px] max-w-[90vw] z-[300]" onClick={(e) => e.stopPropagation()}>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium mb-1">Edit Content</p>
            <p className="text-xs text-muted-foreground">Key: {contentKey}</p>
            {category && <p className="text-xs text-muted-foreground">Category: {category}</p>}
          </div>
          
          <Tabs value={editorMode} onValueChange={(v) => setEditorMode(v as 'plain' | 'rich')}>
            <TabsList className="grid w-full grid-cols-2 mb-2">
              <TabsTrigger value="plain">Plain Text</TabsTrigger>
              <TabsTrigger value="rich">Rich Text (Format)</TabsTrigger>
            </TabsList>
            
            <TabsContent value="plain" className="mt-0">
              <Textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="min-h-[200px]"
                onClick={(e) => e.stopPropagation()}
              />
            </TabsContent>
            
            <TabsContent value="rich" className="mt-0">
              <RichTextEditor
                value={editedContent}
                onChange={setEditedContent}
              />
            </TabsContent>
          </Tabs>
          
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave}>
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
